import { NgFor, NgIf } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  inject,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { TranslocoModule } from "@jsverse/transloco";
import { AuthService } from "app/core/auth/auth.service";
import { TenantOption } from "app/core/auth/tenant.types";
import { Subject, takeUntil } from "rxjs";
import { debounceTime, distinctUntilChanged, switchMap, tap } from "rxjs/operators";

const SEARCH_DEBOUNCE_MS = 400;
const PAGE_SIZE = 25;

export interface TenantSwitchModalData {
  current: TenantOption | null;
}

@Component({
  selector: "app-tenant-switch-modal",
  templateUrl: "./tenant-switch-modal.component.html",
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    NgFor,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    TranslocoModule,
  ],
})
export class TenantSwitchModalComponent implements OnInit, OnDestroy {
  searchText = "";
  tenants: TenantOption[] = [];
  loading = true;
  loadingMore = false;
  total = 0;

  private _authService = inject(AuthService);
  private _changeDetectorRef = inject(ChangeDetectorRef);
  private _search$ = new Subject<string>();
  private _unsubscribeAll = new Subject<void>();
  private _currentSearch = "";

  constructor(
    public dialogRef: MatDialogRef<TenantSwitchModalComponent, TenantOption>,
    @Inject(MAT_DIALOG_DATA) public data: TenantSwitchModalData,
  ) {}

  get canLoadMore(): boolean {
    return !this.loading && !this.loadingMore && this.tenants.length < this.total;
  }

  ngOnInit(): void {
    this._search$
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_MS),
        distinctUntilChanged(),
        tap((search) => {
          this._currentSearch = search;
          this.loading = true;
          this._changeDetectorRef.markForCheck();
        }),
        switchMap((search) => this._authService.myTenants(search, PAGE_SIZE, 0)),
        takeUntil(this._unsubscribeAll),
      )
      .subscribe((response) => {
        this.tenants = response.success ? (response.data ?? []) : [];
        this.total = response.success ? response.page.total : 0;
        this.loading = false;
        this._changeDetectorRef.markForCheck();
      });

    this._search$.next("");
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  onSearch(): void {
    this._search$.next(this.searchText.trim());
  }

  loadMore(): void {
    if (!this.canLoadMore) {
      return;
    }

    this.loadingMore = true;
    this._changeDetectorRef.markForCheck();

    this._authService
      .myTenants(this._currentSearch, PAGE_SIZE, this.tenants.length)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((response) => {
        if (response.success) {
          this.tenants = [...this.tenants, ...(response.data ?? [])];
          this.total = response.page.total;
        }

        this.loadingMore = false;
        this._changeDetectorRef.markForCheck();
      });
  }

  select(tenant: TenantOption): void {
    if (tenant.current) {
      return;
    }

    this.dialogRef.close(tenant);
  }
}
